/**
 * OverrideBanner Component
 *
 * Shows a floating banner at the bottom of the screen when any assumptions
 * have been overridden. Explains that values may differ from the article text
 * and provides a button to reset all overrides.
 */

import React from 'react';
import { useGraph } from '../GraphContext';

export interface OverrideBannerProps {
  /** Custom message (optional) */
  message?: string;
}

export function OverrideBanner({
  message = "You're viewing a custom scenario. Some values may differ from the article text.",
}: OverrideBannerProps): React.ReactElement | null {
  const { overrides, clearAllOverrides } = useGraph();

  // Don't show if no overrides
  if (overrides.size === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white px-4 py-3 shadow-lg">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <svg
            className="w-5 h-5 text-gray-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
          <span className="text-sm">
            <span className="font-medium">Custom scenario</span>
            <span className="text-gray-300 ml-2 hidden sm:inline">{message}</span>
          </span>
          <span className="text-xs text-gray-400">
            ({overrides.size} adjustment{overrides.size !== 1 ? 's' : ''})
          </span>
        </div>
        <button
          onClick={clearAllOverrides}
          className="px-3 py-1.5 text-sm bg-white text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
        >
          Reset all
        </button>
      </div>
    </div>
  );
}

export default OverrideBanner;
